import type { Channel } from "@elaso/shared/types/db";
import type { MandatoryServerActionMessage } from "@elaso/shared/types/web-socket";
import { captureException } from "@sentry/react";
import { enableMapSet, type WritableDraft } from "immer";
import type { Room } from "livekit-client";
import { toast } from "sonner";
import SuperJSON from "superjson";
import { ulid } from "ulid";
import { createStore } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { type GridRoutes, gridRoutes } from "@/grid-routes";
import { SENTRY_ENABLED } from "@/lib/sentry";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";
import { ws } from "@/lib/websocket-wrapper";
import {
	CURRENT_SCHEMA_VERSION,
	migrateState,
	type PersistedState,
} from "./ui-store-migrations";

enableMapSet();

export type ThreadSidePanel = {
	type: "thread";
	threadId: string;
};

export type CreateThreadSidePanel = {
	type: "create-thread";
	replyToMessageId?: string;
};

export type SidePanel = ThreadSidePanel | CreateThreadSidePanel;

export type SetOrUpdate<T> = T | ((prev: NoInfer<T>) => NoInfer<T>);

export type ChannelViewOption = {
	channelId: string;
	sidePanel?: SidePanel;
};

export type GridItem = {
	id: string;
	tabs: {
		type: GridRoutes["type"];
		params: GridRoutes["params"];
	}[];
	activeTabIndex: number;
	x: number;
	y: number;
	w: number;
	h: number;
};

export type ActiveChannel = {
	token: string;
	name: string;
	id: string;
};

export type ActiveBroadcastViewer = {
	token: string;
	canPublish: boolean;
};

export type BackgroundType = "none" | "blur" | "image";

export type BackgroundSettings = {
	type: BackgroundType;
	imageUrl: string | null;
	blurAmount: number;
};

export type FloatingPanelPosition = {
	x: number;
	y: number;
	w: number;
	h: number;
};

export type DeviceSelections = {
	audioinput: string | null;
	audiooutput: string | null;
	videoinput: string | null;
};

export type BroadcastControlSettings = {
	isMicEnabled: boolean;
	isCameraEnabled: boolean;
	isScreenShareEnabled: boolean;
};

export type ScreenShareIndicatorState = {
	sourceName: string | null;
	isVisible: boolean;
	position: { x: number; y: number };
};

export type SendKeyMode = "enter" | "cmd-enter";

export type NotificationSoundOption = "bloop" | "plink";
export type NotificationSoundMode = "all" | "mentions-only" | "off";

export type EmojiUsage = Record<string, number>;

export type PendingMove = {
	channelName: string;
	isPullTogether: boolean;
} | null;

export type KeybindAction =
	| "toggleSidebar"
	| "bold"
	| "italic"
	| "strikethrough"
	| "inlineCode"
	| "link";

export type Keybind = {
	key: string;
	metaKey?: boolean;
	ctrlKey?: boolean;
	shiftKey?: boolean;
	altKey?: boolean;
};

export type Keybinds = Record<KeybindAction, Keybind>;

export const DEFAULT_KEYBINDS: Keybinds = {
	toggleSidebar: { key: "\\", metaKey: true },
	bold: { key: "b", metaKey: true },
	italic: { key: "i", metaKey: true },
	strikethrough: { key: "x", metaKey: true, shiftKey: true },
	inlineCode: { key: "`", metaKey: true },
	link: { key: "k", metaKey: true },
};

export type Drafts = Map<string, Record<string, unknown>>;

export type PendingMoveState = PendingMove;

export const KEYBIND_LABELS: Record<KeybindAction, string> = {
	toggleSidebar: "Toggle Sidebar",
	bold: "Bold",
	italic: "Italic",
	strikethrough: "Strikethrough",
	inlineCode: "Inline Code",
	link: "Link",
};

export const formatKeybind = (keybind: Keybind): string => {
	const parts: string[] = [];
	if (keybind.metaKey) parts.push("Cmd");
	if (keybind.ctrlKey) parts.push("Ctrl");
	if (keybind.altKey) parts.push("Alt");
	if (keybind.shiftKey) parts.push("Shift");
	parts.push(keybind.key.toUpperCase());
	return parts.join(" + ");
};

export const keybindMatches = (
	keybind: Keybind,
	event: KeyboardEvent
): boolean => {
	const keyMatches = event.key.toLowerCase() === keybind.key.toLowerCase();
	const metaMatches = !!keybind.metaKey === event.metaKey;
	const ctrlMatches = !!keybind.ctrlKey === event.ctrlKey;
	const shiftMatches = !!keybind.shiftKey === event.shiftKey;
	const altMatches = !!keybind.altKey === event.altKey;
	return keyMatches && metaMatches && ctrlMatches && shiftMatches && altMatches;
};

export type PanelOrientation = "horizontal" | "vertical";

export type CombinedLayoutMode =
	| "split-vertical"
	| "split-horizontal"
	| "tabs"
	| "spotlight-voice"
	| "spotlight-broadcast";

type UIStoreState = {
	currentWorkspaceId: string | null;
	channelViewOptions: Map<string, ChannelViewOption>;
	gridItems: GridItem[];
	activeChannel: ActiveChannel | null;
	room: Room | null;
	backgroundSettings: BackgroundSettings;
	floatingPanelPosition: FloatingPanelPosition;
	deviceSelections: DeviceSelections;
	activeBroadcastViewer: ActiveBroadcastViewer | null;
	broadcastRoom: Room | null;
	broadcastVolume: number;
	broadcastControlSettings: BroadcastControlSettings;
	screenShareIndicator: ScreenShareIndicatorState;
	pipOpenFn: (() => Promise<boolean>) | null;
	panelOrientation: PanelOrientation;
	combinedLayoutMode: CombinedLayoutMode;
	combinedActiveTab: "voice" | "broadcast";
	isBroadcastAudioActive: boolean;
	spotlightOverlayPosition: FloatingPanelPosition;
	runTour: boolean;
	sendKeyMode: SendKeyMode;
	notificationSoundMode: NotificationSoundMode;
	notificationSound: NotificationSoundOption;
	mentionNotificationSound: NotificationSoundOption;
	collapsedCategories: Set<string>;
	// emoji -> usage count
	emojiUsage: EmojiUsage;
	// channelId -> TipTap JSON content
	drafts: Drafts;
	keybinds: Keybinds;
	pendingMove: PendingMoveState;
};

type UIStoreActions = {
	setCurrentWorkspaceId: (workspaceId: string | null) => void;
	setChannelViewOption: (
		channelId: string,
		view: SetOrUpdate<ChannelViewOption>
	) => void;
	setActiveChannel: (channel: ActiveChannel | null) => void;
	/**
	 * Leave every LiveKit room this client is in (the voice/video room and the
	 * broadcast room) and forget the active channel (TECH-3259).
	 *
	 * Used when access to the workspace is withdrawn mid-session. Unlike the
	 * teardown inside `setCurrentWorkspaceId` this keeps no destination. There is
	 * no workspace to move to, only rooms to get out of. Safe to call with
	 * nothing connected.
	 */
	disconnectAllRooms: () => void;
	navigate: (to: GridRoutes, options?: { newTab?: boolean }) => void;
	setActiveTab: (gridItemId: string, tabIndex: number) => void;
	closeTab: (gridItemId: string, tabIndex: number) => void;
	// Voice and video
	setRoom: (room: Room | null) => void;
	handleLeaveChannel: () => Promise<void>;
	setBackgroundSettings: (settings: Partial<BackgroundSettings>) => void;
	setDeviceSelection: (kind: MediaDeviceKind, deviceId: string) => void;
	handleJoinVoiceChannel: (
		channel: Channel,
		options?: { newTab?: boolean }
	) => Promise<void>;
	refreshActiveChannelToken: () => Promise<void>;
	setFloatingPanelPosition: (position: Partial<FloatingPanelPosition>) => void;
	setActiveBroadcastViewer: (viewer: ActiveBroadcastViewer | null) => void;
	setBroadcastRoom: (room: Room | null) => void;
	setBroadcastVolume: (volume: number) => void;
	handleJoinBroadcast: (requestSpeakerPermissions?: boolean) => Promise<void>;
	refreshActiveBroadcastViewerToken: () => Promise<void>;
	handleLeaveBroadcast: () => void;
	handleStopBroadcast: () => Promise<void>;
	setBroadcastControlSettings: (
		settings: Partial<BroadcastControlSettings>
	) => void;
	setScreenShareIndicator: (state: Partial<ScreenShareIndicatorState>) => void;
	setPipOpenFn: (fn: (() => Promise<boolean>) | null) => void;
	setPanelOrientation: (orientation: PanelOrientation) => void;
	setCombinedLayoutMode: (mode: CombinedLayoutMode) => void;
	setCombinedActiveTab: (tab: "voice" | "broadcast") => void;
	setIsBroadcastAudioActive: (active: boolean) => void;
	setSpotlightOverlayPosition: (
		position: Partial<FloatingPanelPosition>
	) => void;
	startTour: () => void;
	stopTour: () => void;
	setSendKeyMode: (mode: SendKeyMode) => void;
	setNotificationSoundMode: (mode: NotificationSoundMode) => void;
	setNotificationSound: (sound: NotificationSoundOption) => void;
	setMentionNotificationSound: (sound: NotificationSoundOption) => void;
	setCategoryCollapsed: (categoryId: string, collapsed: boolean) => void;
	trackEmojiUsage: (emoji: string) => void;
	getFavouriteEmojis: (limit?: number) => string[];
	setDraft: (channelId: string, content: Record<string, unknown>) => void;
	deleteDraft: (channelId: string) => void;
	setKeybind: (action: KeybindAction, keybind: Keybind) => void;
	resetKeybinds: () => void;
	setPendingMove: (move: PendingMoveState) => void;
	reset: () => void;
};

type UIStore = UIStoreState & UIStoreActions;

const defaultState: UIStoreState = {
	currentWorkspaceId: null,
	channelViewOptions: new Map(),
	gridItems: [],
	activeChannel: null,
	room: null,
	backgroundSettings: {
		type: "blur",
		imageUrl: null,
		blurAmount: 20,
	},
	floatingPanelPosition: { x: 0, y: 0, w: 320, h: 192 },
	deviceSelections: {
		audioinput: null,
		audiooutput: null,
		videoinput: null,
	},
	activeBroadcastViewer: null,
	broadcastRoom: null,
	broadcastVolume: 1,
	broadcastControlSettings: {
		isMicEnabled: false,
		isCameraEnabled: false,
		isScreenShareEnabled: false,
	},
	screenShareIndicator: {
		sourceName: null,
		isVisible: true,
		position: { x: -1, y: -1 },
	},
	pipOpenFn: null,
	panelOrientation: "vertical",
	combinedLayoutMode: "tabs",
	combinedActiveTab: "voice",
	isBroadcastAudioActive: false,
	spotlightOverlayPosition: { x: -1, y: -1, w: 400, h: 260 },
	runTour: false,
	sendKeyMode: "enter",
	notificationSoundMode: "all",
	notificationSound: "bloop",
	mentionNotificationSound: "plink",
	collapsedCategories: new Set(),
	emojiUsage: {},
	drafts: new Map(),
	keybinds: { ...DEFAULT_KEYBINDS },
	pendingMove: null,
};

const customStorage = {
	getItem: (name: string) => {
		const data = localStorage.getItem(name);
		if (!data) return null;

		try {
			const deserialized = SuperJSON.parse(data) as {
				state: PersistedState;
				version?: number;
			};

			const migratedState = migrateState(deserialized.state ?? {});

			return {
				...deserialized,
				state: migratedState,
			};
		} catch (error) {
			console.error("[ui-store] Failed to parse stored state:", error);
			if (SENTRY_ENABLED)
				captureException(error, { tags: { context: "ui_store_parse" } });
			localStorage.removeItem(name);
			return null;
		}
	},
	setItem: (name: string, state: unknown) => {
		localStorage.setItem(name, SuperJSON.stringify(state));
	},
	removeItem: (name: string) => {
		localStorage.removeItem(name);
	},
} as const;

export const uiStore = createStore<UIStore>()(
	immer(
		persist(
			(set, get) => ({
				...defaultState,
				setCurrentWorkspaceId: (workspaceId) => {
					const currentState = get();

					// No-op when the workspace is unchanged. The reset below (closing
					// tabs, clearing drafts, disconnecting rooms) is switch-only
					// behaviour; firing it for the same id, for example the connect chain
					// re-selecting the persisted workspace on refresh, would wipe the
					// restored gridItems.
					if (workspaceId === currentState.currentWorkspaceId) return;

					if (currentState.room) {
						const localTracks = Array.from(
							currentState.room.localParticipant.trackPublications.values()
						);
						for (const publication of localTracks) {
							if (publication.track) {
								publication.track.stop();
							}
						}
						currentState.room.disconnect();
					}

					if (currentState.broadcastRoom) {
						currentState.broadcastRoom.disconnect();
					}

					set((state) => {
						state.currentWorkspaceId = workspaceId;
						state.channelViewOptions.clear();
						state.gridItems = [];
						state.activeChannel = null;
						state.room = null;
						state.activeBroadcastViewer = null;
						state.broadcastRoom = null;
						state.collapsedCategories.clear();
						state.drafts.clear();
					});
				},
				setChannelViewOption: (
					channelId: string,
					view: SetOrUpdate<ChannelViewOption>
				) => {
					set((state) => {
						state.channelViewOptions.set(
							channelId,
							typeof view === "function"
								? view(
										state.channelViewOptions.get(channelId) ?? {
											channelId,
										}
									)
								: view
						);
					});
				},
				navigate: ({ type, params }, options) => {
					const route = gridRoutes[type];

					if (!route) {
						console.error(`Route "${type}" not found`);
						return;
					}

					// TODO: Re-enable mobile tab restriction if needed
					// const isMobile = window.matchMedia("(max-width: 767px)").matches;

					const currentState = get();
					const currentTabType =
						currentState.gridItems.length > 0
							? currentState.gridItems[0]?.tabs[
									currentState.gridItems[0]?.activeTabIndex
								]?.type
							: null;

					const isLeavingMedia =
						currentTabType === "voiceChannel" ||
						currentTabType === "broadcastControls";
					const isGoingToMedia =
						type === "voiceChannel" || type === "broadcastControls";

					const shouldOpenPip = isLeavingMedia && !isGoingToMedia;

					if (shouldOpenPip && currentState.pipOpenFn) {
						currentState.pipOpenFn();
					}

					set((state) => {
						const newTab = {
							type,
							params,
						};

						const isSameTab = (tab1: typeof newTab, tab2: typeof newTab) =>
							tab1.type === tab2.type &&
							JSON.stringify(tab1.params) === JSON.stringify(tab2.params);

						if (state.gridItems.length === 0) {
							state.gridItems = [
								{
									x: 0,
									y: 0,
									w: 1,
									h: 1,
									id: ulid(),
									tabs: [newTab],
									activeTabIndex: 0,
								},
							];
						} else if (
							// TODO: Re-enable mobile tab restriction if needed
							options?.newTab ||
							type === "voiceChannel" ||
							type === "broadcastControls"
						) {
							const firstItem = state.gridItems[0];
							if (firstItem) {
								const existingTabIndex = firstItem.tabs.findIndex((tab) =>
									isSameTab(tab, newTab)
								);

								if (existingTabIndex !== -1) {
									firstItem.activeTabIndex = existingTabIndex;
								} else {
									firstItem.tabs.push(newTab);
									firstItem.activeTabIndex = firstItem.tabs.length - 1;
								}
							}
						} else {
							const firstItem = state.gridItems[0];
							if (firstItem) {
								const existingTabIndex = firstItem.tabs.findIndex((tab) =>
									isSameTab(tab, newTab)
								);

								if (existingTabIndex !== -1) {
									firstItem.activeTabIndex = existingTabIndex;
								} else if (firstItem.tabs.length > 0) {
									firstItem.tabs[firstItem.tabs.length - 1] = newTab;
									firstItem.activeTabIndex = firstItem.tabs.length - 1;
								}
							}
						}
					});
				},
				setActiveTab: (gridItemId, tabIndex) => {
					const currentState = get();
					const item = currentState.gridItems.find((i) => i.id === gridItemId);
					if (!item || tabIndex < 0 || tabIndex >= item.tabs.length) return;

					const currentTab = item.tabs[item.activeTabIndex];
					const nextTab = item.tabs[tabIndex];

					const isLeavingMedia =
						currentTab?.type === "voiceChannel" ||
						currentTab?.type === "broadcastControls";
					const isGoingToMedia =
						nextTab?.type === "voiceChannel" ||
						nextTab?.type === "broadcastControls";

					const shouldOpenPip = isLeavingMedia && !isGoingToMedia;

					if (shouldOpenPip && currentState.pipOpenFn) {
						currentState.pipOpenFn();
					}

					set((state) => {
						const stateItem = state.gridItems.find((i) => i.id === gridItemId);
						if (
							stateItem &&
							tabIndex >= 0 &&
							tabIndex < stateItem.tabs.length
						) {
							stateItem.activeTabIndex = tabIndex;
						}
					});
				},
				closeTab: (gridItemId, tabIndex) => {
					const currentState = get();
					const item = currentState.gridItems.find((i) => i.id === gridItemId);
					if (!item) return;

					const closingTab = item.tabs[tabIndex];
					const isClosingMedia =
						closingTab?.type === "voiceChannel" ||
						closingTab?.type === "broadcastControls";

					const isClosingActiveTab = tabIndex === item.activeTabIndex;
					const remainingTabs = item.tabs.filter((_, i) => i !== tabIndex);
					const nextActiveTab = isClosingActiveTab
						? remainingTabs[Math.min(tabIndex, remainingTabs.length - 1)]
						: null;
					const nextIsMedia =
						nextActiveTab?.type === "voiceChannel" ||
						nextActiveTab?.type === "broadcastControls";

					const shouldOpenPip =
						isClosingActiveTab && isClosingMedia && !nextIsMedia;

					if (shouldOpenPip && currentState.pipOpenFn) {
						currentState.pipOpenFn();
					}

					set((state) => {
						const stateItem = state.gridItems.find((i) => i.id === gridItemId);
						if (!stateItem) return;

						stateItem.tabs.splice(tabIndex, 1);

						if (stateItem.tabs.length === 0) {
							state.gridItems = state.gridItems.filter(
								(i) => i.id !== gridItemId
							);
						} else if (stateItem.activeTabIndex >= stateItem.tabs.length) {
							stateItem.activeTabIndex = stateItem.tabs.length - 1;
						}
					});
				},
				disconnectAllRooms: () => {
					const currentState = get();

					if (currentState.room) {
						// Stop the local tracks before disconnecting so the camera light
						// goes out rather than lingering until the tab is closed.
						const localTracks = Array.from(
							currentState.room.localParticipant.trackPublications.values()
						);
						for (const publication of localTracks) {
							if (publication.track) {
								publication.track.stop();
							}
						}
						currentState.room.disconnect();
					}

					if (currentState.broadcastRoom) {
						currentState.broadcastRoom.disconnect();
					}

					set((state) => {
						state.room = null;
						state.broadcastRoom = null;
						state.activeChannel = null;
					});
				},
				setActiveChannel: (channel) => {
					set((state) => {
						state.activeChannel = channel;
					});
				},
				setRoom: (room) => {
					set((state) => {
						state.room = room as WritableDraft<Room> | null;
					});
				},
				handleLeaveChannel: async () => {
					const currentState = get();
					if (currentState.room) {
						const localTracks = Array.from(
							currentState.room.localParticipant.trackPublications.values()
						);
						for (const publication of localTracks) {
							if (publication.track) {
								publication.track.stop();
							}
						}
						currentState.room.disconnect();
					}

					const { broadcast } = await import("@/lib/stores/core-store").then(
						(m) => m.coreStore.getState()
					);
					const hasBroadcast =
						broadcast &&
						(broadcast.status === "live" || broadcast.status === "paused") &&
						currentState.broadcastRoom;

					set((state) => {
						state.activeChannel = null;
						state.room = null;
						if (!hasBroadcast) {
							for (const item of state.gridItems) {
								const voiceTabIndex = item.tabs.findIndex(
									(tab) => tab.type === "voiceChannel"
								);
								if (voiceTabIndex !== -1) {
									item.tabs.splice(voiceTabIndex, 1);
									if (item.activeTabIndex >= item.tabs.length) {
										item.activeTabIndex = Math.max(0, item.tabs.length - 1);
									}
								}
							}
							state.gridItems = state.gridItems.filter(
								(item) => item.tabs.length > 0
							);
						}
					});
				},
				setBackgroundSettings: (settings) => {
					set((state) => {
						state.backgroundSettings = {
							...state.backgroundSettings,
							...settings,
						};
					});
				},
				setDeviceSelection: (kind, deviceId) => {
					set((state) => {
						state.deviceSelections[kind] = deviceId;
					});
				},
				setFloatingPanelPosition: (position) => {
					set((state) => {
						state.floatingPanelPosition = {
							...state.floatingPanelPosition,
							...position,
						};
					});
				},
				setActiveBroadcastViewer: (viewer) => {
					set((state) => {
						state.activeBroadcastViewer = viewer;
					});
				},
				setBroadcastRoom: (room) => {
					set((state) => {
						state.broadcastRoom = room as WritableDraft<Room> | null;
					});
				},
				setBroadcastVolume: (volume) => {
					set((state) => {
						state.broadcastVolume = volume;
					});
				},
				handleJoinBroadcast: async (requestSpeakerPermissions = false) => {
					const user = authStore.getState().user;
					if (!user?.email) {
						toast.error("You must be logged in to watch a broadcast");
						return;
					}

					try {
						const { generateBroadcastToken } = await import(
							"@/lib/stores/core-store"
						).then((m) => m.coreStore.getState());
						const response = await generateBroadcastToken(
							requestSpeakerPermissions
						);

						set((state) => {
							state.activeBroadcastViewer = {
								token: response.token,
								canPublish: response.canPublish,
							};
						});
					} catch (error) {
						console.error("Failed to join broadcast:", error);
						if (SENTRY_ENABLED)
							captureException(error, {
								tags: { context: "ui_store_join_broadcast" },
							});
						toast.error("Failed to join broadcast. Please try again later");
					}
				},
				refreshActiveBroadcastViewerToken: async () => {
					const current = get().activeBroadcastViewer;
					if (!current) return;
					const { generateBroadcastToken } = coreStore.getState();
					const response = await generateBroadcastToken(current.canPublish);
					set((state) => {
						if (!state.activeBroadcastViewer) return;
						state.activeBroadcastViewer.token = response.token;
						state.activeBroadcastViewer.canPublish = response.canPublish;
					});
				},
				handleLeaveBroadcast: () => {
					const currentState = get();
					if (currentState.broadcastRoom) {
						currentState.broadcastRoom.disconnect();
					}
					const hasActiveVoiceCall = !!currentState.activeChannel;
					set((state) => {
						state.activeBroadcastViewer = null;
						state.broadcastRoom = null;
						if (!hasActiveVoiceCall) {
							for (const item of state.gridItems) {
								const voiceTabIndex = item.tabs.findIndex(
									(tab) => tab.type === "voiceChannel"
								);
								if (voiceTabIndex !== -1) {
									item.tabs.splice(voiceTabIndex, 1);
									if (item.activeTabIndex >= item.tabs.length) {
										item.activeTabIndex = Math.max(0, item.tabs.length - 1);
									}
								}
							}
							state.gridItems = state.gridItems.filter(
								(item) => item.tabs.length > 0
							);
						}
					});
				},
				handleStopBroadcast: async () => {
					const currentState = get();

					if (currentState.broadcastRoom) {
						const localTracks = Array.from(
							currentState.broadcastRoom.localParticipant.trackPublications.values()
						);
						for (const publication of localTracks) {
							if (publication.track) {
								publication.track.stop();
							}
						}
						currentState.broadcastRoom.disconnect();
					}

					const { stopBroadcast } = await import(
						"@/lib/stores/core-store"
					).then((m) => m.coreStore.getState());
					await stopBroadcast();

					const hasActiveVoiceCall = !!currentState.activeChannel;
					set((state) => {
						state.activeBroadcastViewer = null;
						state.broadcastRoom = null;
						if (!hasActiveVoiceCall) {
							for (const item of state.gridItems) {
								const voiceTabIndex = item.tabs.findIndex(
									(tab) => tab.type === "voiceChannel"
								);
								if (voiceTabIndex !== -1) {
									item.tabs.splice(voiceTabIndex, 1);
									if (item.activeTabIndex >= item.tabs.length) {
										item.activeTabIndex = Math.max(0, item.tabs.length - 1);
									}
								}
							}
							state.gridItems = state.gridItems.filter(
								(item) => item.tabs.length > 0
							);
						}
					});

					toast.info("Broadcast ended");
				},
				setBroadcastControlSettings: (settings) => {
					set((state) => {
						state.broadcastControlSettings = {
							...state.broadcastControlSettings,
							...settings,
						};
					});
				},
				setScreenShareIndicator: (indicatorState) => {
					set((state) => {
						state.screenShareIndicator = {
							...state.screenShareIndicator,
							...indicatorState,
						};
					});
				},
				setPipOpenFn: (fn) => {
					set((state) => {
						state.pipOpenFn = fn;
					});
				},
				setPanelOrientation: (orientation) => {
					set((state) => {
						state.panelOrientation = orientation;
					});
				},
				setCombinedLayoutMode: (mode) => {
					set((state) => {
						state.combinedLayoutMode = mode;
					});
				},
				setCombinedActiveTab: (tab) => {
					set((state) => {
						state.combinedActiveTab = tab;
					});
				},
				setIsBroadcastAudioActive: (active) => {
					set((state) => {
						state.isBroadcastAudioActive = active;
					});
				},
				setSpotlightOverlayPosition: (position) => {
					set((state) => {
						state.spotlightOverlayPosition = {
							...state.spotlightOverlayPosition,
							...position,
						};
					});
				},
				startTour: async () => {
					const { resetAllTours } = coreStore.getState();
					await resetAllTours();
					set((state) => {
						state.runTour = true;
					});
				},
				stopTour: () => {
					set((state) => {
						state.runTour = false;
					});
				},
				setSendKeyMode: (mode) => {
					set((state) => {
						state.sendKeyMode = mode;
					});
				},
				setNotificationSoundMode: (mode) => {
					set((state) => {
						state.notificationSoundMode = mode;
					});
				},
				setNotificationSound: (sound) => {
					set((state) => {
						state.notificationSound = sound;
					});
				},
				setMentionNotificationSound: (sound) => {
					set((state) => {
						state.mentionNotificationSound = sound;
					});
				},
				setCategoryCollapsed: (categoryId, collapsed) => {
					set((state) => {
						if (collapsed) {
							state.collapsedCategories.add(categoryId);
						} else {
							state.collapsedCategories.delete(categoryId);
						}
					});
				},
				trackEmojiUsage: (emoji) => {
					set((state) => {
						state.emojiUsage[emoji] = (state.emojiUsage[emoji] ?? 0) + 1;
					});
				},
				getFavouriteEmojis: (limit = 5) => {
					const { emojiUsage } = get();
					return Object.entries(emojiUsage)
						.sort(([, a], [, b]) => b - a)
						.slice(0, limit)
						.map(([emoji]) => emoji);
				},
				setDraft: (channelId, content) => {
					set((state) => {
						state.drafts.set(channelId, content);
					});
				},
				deleteDraft: (channelId) => {
					set((state) => {
						state.drafts.delete(channelId);
					});
				},
				setKeybind: (action, keybind) => {
					set((state) => {
						state.keybinds[action] = keybind;
					});
				},
				resetKeybinds: () => {
					set((state) => {
						state.keybinds = { ...DEFAULT_KEYBINDS };
					});
				},
				setPendingMove: (move) => {
					set((state) => {
						state.pendingMove = move;
					});
				},
				reset: () => {
					set((state) => {
						state.currentWorkspaceId = defaultState.currentWorkspaceId;
						state.channelViewOptions = new Map();
						state.gridItems = [];
						state.activeChannel = null;
						state.room = null;
						state.activeBroadcastViewer = null;
						state.broadcastRoom = null;
						state.collapsedCategories = new Set();
						state.drafts = new Map();
						state.runTour = false;
					});
				},
				handleJoinVoiceChannel: async (channel, options) => {
					const user = authStore.getState().user;
					if (!user?.email) {
						toast.error("You must be logged in to join a voice channel");
						return;
					}

					const currentState = get();

					if (currentState.activeChannel?.id === channel.id) {
						get().navigate({ type: "voiceChannel" }, options);
						return;
					}

					if (currentState.room) {
						const localTracks = Array.from(
							currentState.room.localParticipant.trackPublications.values()
						);
						for (const publication of localTracks) {
							if (publication.track) {
								publication.track.stop();
							}
						}
						currentState.room.disconnect();
						set((state) => {
							state.room = null;
						});
					}

					try {
						const requestId = ulid();
						const response = await ws.sendWithPromise<{ token: string }>({
							id: requestId,
							procedure: "livekit.generateToken",
							data: {
								roomName: channel.id,
								channelName: channel.name,
							},
						});

						set((state) => {
							state.activeChannel = {
								token: response.token,
								name: channel.name,
								id: channel.id,
							};
							state.combinedActiveTab = "voice";
						});

						// The stage snapshot is fetched by `useVoiceStageActiveAsset`,
						// which orders it after the active-topic declare so nothing is
						// published into the gap between the two (TECH-3119).

						get().navigate(
							{
								type: "voiceChannel",
							},
							options
						);
					} catch (error) {
						console.error("Failed to join voice channel:", error);
						// CONFLICT carries the server's capacity message ("Channel is
						// full. Maximum N participants allowed."), so show it as-is.
						// "please try again" is the wrong advice for a full room.
						const isChannelFull =
							(error as Error & { code?: string })?.code === "CONFLICT";
						const errorMessage = isChannelFull
							? error instanceof Error && error.message
								? error.message
								: "Channel is full"
							: error instanceof Error && error.message === "Forbidden"
								? "You don't have permission to join this channel"
								: "Failed to join voice channel. Please try again";
						// A full or forbidden channel is expected behavior, not an error
						// worth a Sentry event.
						const isExpected =
							isChannelFull ||
							errorMessage === "You don't have permission to join this channel";
						if (SENTRY_ENABLED && !isExpected)
							captureException(error, {
								tags: { context: "ui_store_join_voice" },
							});
						toast.error(errorMessage);
					}
				},
				refreshActiveChannelToken: async () => {
					const current = get().activeChannel;
					if (!current) return;
					const response = await ws.sendWithPromise<{ token: string }>({
						id: ulid(),
						procedure: "livekit.generateToken",
						data: {
							roomName: current.id,
							channelName: current.name,
						},
					});
					set((state) => {
						if (!state.activeChannel) return;
						state.activeChannel.token = response.token;
					});
				},
			}),
			{
				name: "elaso-ui-store",
				storage: customStorage as any,
				partialize: (state) => ({
					_schemaVersion: CURRENT_SCHEMA_VERSION,
					currentWorkspaceId: state.currentWorkspaceId,
					channelViewOptions: state.channelViewOptions,
					gridItems: state.gridItems
						.map((item) => {
							const filteredTabs = item.tabs.filter(
								(tab) => tab.type !== "voiceChannel"
							);
							if (filteredTabs.length === 0) return null;
							return {
								...item,
								tabs: filteredTabs,
								activeTabIndex: Math.min(
									item.activeTabIndex,
									filteredTabs.length - 1
								),
							};
						})
						.filter(Boolean) as typeof state.gridItems,
					backgroundSettings: state.backgroundSettings,
					floatingPanelPosition: state.floatingPanelPosition,
					deviceSelections: state.deviceSelections,
					panelOrientation: state.panelOrientation,
					combinedLayoutMode: state.combinedLayoutMode,
					spotlightOverlayPosition: state.spotlightOverlayPosition,
					sendKeyMode: state.sendKeyMode,
					notificationSoundMode: state.notificationSoundMode,
					notificationSound: state.notificationSound,
					mentionNotificationSound: state.mentionNotificationSound,
					collapsedCategories: state.collapsedCategories,
					emojiUsage: state.emojiUsage,
					drafts: state.drafts,
					keybinds: state.keybinds,
				}),
			}
		)
	)
);

let pendingMoveTimer: ReturnType<typeof setTimeout> | null = null;

ws.addEventListener("message", (event) => {
	const message = JSON.parse((event as MessageEvent).data as string);
	if (message.type !== "MANDATORY_SERVER_ACTION") return;

	const action = message as MandatoryServerActionMessage;

	switch (action.action) {
		case "MOVE_TO_CHANNEL": {
			const state = uiStore.getState();
			if (state.activeChannel?.id !== action.sourceChannelId) return;

			if (pendingMoveTimer !== null) {
				clearTimeout(pendingMoveTimer);
				pendingMoveTimer = null;
			}

			const countdown = action.countdown ?? 3000;
			const isPullTogether = countdown > 3000;

			uiStore.getState().setPendingMove({
				channelName: action.channelName,
				isPullTogether,
			});

			pendingMoveTimer = setTimeout(() => {
				pendingMoveTimer = null;
				const currentState = uiStore.getState();

				uiStore.getState().setPendingMove(null);

				// Re-check. The user may have left the channel during the countdown.
				if (currentState.activeChannel?.id !== action.sourceChannelId) return;

				if (currentState.room) {
					const localTracks = Array.from(
						currentState.room.localParticipant.trackPublications.values()
					);
					for (const publication of localTracks) {
						if (publication.track) {
							publication.track.stop();
						}
					}
					currentState.room.disconnect();
				}

				uiStore.setState({
					activeChannel: {
						token: action.token,
						name: action.channelName,
						id: action.channelId,
					},
					room: null,
					combinedActiveTab: "voice",
				});

				uiStore.getState().navigate({ type: "voiceChannel" });
			}, countdown);
			break;
		}
	}
});
